import * as React from 'react';

import type { NormSetPeer } from '@graphql/queries/PerceptionModelQueries';
import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';

import AnchorList from './AnchorList';
import MemberTable from './MemberTable';
import PerceptionDataExplorer from './PerceptionDataExplorer';
import SensorySignature from './SensorySignature';
import type { TokenFamily, TokenSummary } from './tokenProfile';

/**
 * A norm set, looked at rather than changed.
 *
 * Shared by the builder and the saved norm set page, so what you inspect before
 * saving is the view you get afterwards. The builder feeds it from
 * `buildNormSet` — the same call `onSave` makes — which is what makes that
 * claim exact rather than approximate.
 *
 * Nothing here edits. The builder keeps a separate editing path behind its own
 * control; an affordance here would re-merge the two intents that split exists
 * to separate.
 */

type Props = {
  /** the job this norm set is pinned to — the map's universe */
  jobId: number;
  anchors: string[];
  members: NormSetPeer[];
  /**
   * The sensory signature, when this view is the one responsible for showing
   * it. The builder's summary already carries it directly above, so the builder
   * passes nothing and the section does not render twice.
   */
  families?: Array<{ family: TokenFamily; tokens: TokenSummary[] }>;
  /** false where the anchors' own tokens were never loaded to compare against */
  markNovel?: boolean;
  /**
   * Offered only where anchors can actually move — the saved norm set. The
   * builder has its own picker above, and a second one here would be two
   * controls for one decision.
   */
  onChangeAnchors?: () => void;
};

const NormSetView: React.FC<Props> = ({
  jobId,
  anchors,
  members,
  families,
  markNovel = true,
  onChangeAnchors,
}) => {
  // The index behind the map is ~1.1 MB at 16k products, so it is fetched when
  // the map is opened rather than on every visit. Collapsed is also the honest
  // default: the anchors and the taste are what this view is answering.
  const [openMap, setOpenMap] = React.useState(false);
  const [openMembers, setOpenMembers] = React.useState(false);

  const anchorNames = React.useMemo(() => new Set(anchors), [anchors]);
  const included = React.useMemo(
    () => new Set(members.map((m) => m.product)),
    [members],
  );

  const section = {
    disableGutters: true,
    elevation: 0,
    sx: {
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      '&::before': { display: 'none' },
    },
  } as const;

  return (
    <>
      <AnchorList
        anchors={anchors}
        caption={`Every distance in this norm set was measured from ${
          anchors.length === 1 ? 'this product' : 'these products'
        }.`}
        onChangeAnchors={onChangeAnchors}
      />

      {families && (
        <SensorySignature
          families={families}
          total={members.length}
          markNovel={markNovel}
        />
      )}

      <Accordion
        expanded={openMembers}
        onChange={() => setOpenMembers((v) => !v)}
        {...section}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box>
            {/* "Norm set products", not "Members": the products *are* the norm
                set, and the translation page already calls the same list that.
                Two words for one thing read as two things — qualified because
                the page header above is already "Norm set". */}
            <Typography variant="body2">Norm set products</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              All {members.length}, nearest first
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {openMembers && <MemberTable members={members} />}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={openMap}
        onChange={() => setOpenMap((v) => !v)}
        {...section}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box>
            <Typography variant="body2">Where these products sit</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              The {members.length} members and {anchors.length} anchor
              {anchors.length === 1 ? '' : 's'}, on the map this model laid out
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Mounted only once opened: the explorer's index query is the
              expensive read here, and a collapsed panel that has already paid
              for it is not collapsed in any useful sense. */}
          {openMap && (
            <PerceptionDataExplorer
              jobId={jobId}
              anchorNames={anchorNames}
              included={included}
              highlightByTaste={false}
              focusOnSet
            />
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default NormSetView;
